import React, { useState, useEffect } from 'react';
import { Check, Sparkles, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { getPackages } from '../api';

const Pricing = () => {
  const { t, i18n } = useTranslation();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const data = await getPackages();
        setPackages(data);
      } catch (err) {
        setError(t('pricing.error'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [t, i18n.language]);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section id="pricing" className="py-24 bg-gradient-to-b from-pink-50 to-white">
        <div className="container mx-auto px-4 flex justify-center items-center min-h-[400px]">
          <Loader2 className="w-12 h-12 animate-spin text-pink-500" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="pricing" className="py-24 bg-gradient-to-b from-pink-50 to-white">
        <div className="container mx-auto px-4 text-center">
          <p className="text-red-500">{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="pricing" className="py-24 bg-gradient-to-b from-pink-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-orange-500 via-pink-600 to-fuchsia-600 bg-clip-text text-transparent">
              {t("pricing.title")}
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            {t("pricing.subtitle")}
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {packages.map((pkg, index) => (
            <Card
              key={pkg.id}
              className={`relative hover:shadow-2xl transition-all duration-300 border-0 ${
                pkg.popular
                  ? 'shadow-2xl scale-105 border-4 border-purple-200'
                  : 'shadow-lg'
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Popular Badge */}
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-orange-500 to-pink-600 text-white px-6 py-2 text-sm font-bold shadow-lg">
                    <Sparkles className="w-4 h-4 mr-1 inline" />
                    {t("pricing.premium.popular")}
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-8 pt-8">
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  {i18n.language === 'en' ? (pkg.name_en || pkg.name) : pkg.name}
                </CardTitle>
                <CardDescription className="text-gray-600">
                  {i18n.language === 'en' ? (pkg.description_en || pkg.description) : pkg.description}
                </CardDescription>
                <div className="mt-6">
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-5xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
                      {pkg.price}$
                    </span>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {(i18n.language === 'en' ? (pkg.features_en || pkg.features) : pkg.features).map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-gradient-to-r ${pkg.color}`}>
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </CardContent>

              <CardFooter className="pt-8">
                <Button
                  onClick={scrollToContact}
                  className={`w-full py-6 rounded-full text-lg font-medium transition-all ${
                    pkg.popular
                      ? 'bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 text-white shadow-xl'
                      : 'bg-white text-orange-600 border-2 border-orange-600 hover:bg-orange-50'
                  }`}
                >
                  {t("pricing.cta")}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Custom Quote CTA */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-orange-100 via-pink-100 to-fuchsia-100 rounded-2xl p-8 max-w-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t("pricing.needCustom")}
            </h3>
            <p className="text-gray-600 mb-6">
              {t("pricing.customDescription")}
            </p>
            <Button
              onClick={scrollToContact}
              size="lg"
              className="bg-gradient-to-r from-orange-500 via-pink-600 to-fuchsia-600 hover:from-orange-600 hover:via-pink-700 hover:to-fuchsia-700 text-white px-8 py-6 rounded-full shadow-xl"
            >
              {t("pricing.customCta")}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
